import { motion } from 'motion/react';
import {
  ArrowRight,
  MessageSquare,
  Share2,
  ShoppingBag,
  Briefcase,
  Building2,
  Wallet,
  Zap,
  CheckCircle2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../utils/helpers';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-900 selection:bg-neutral-900 selection:text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-neutral-100 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-neutral-900 rounded-xl flex items-center justify-center shadow-lg shadow-neutral-900/20">
              <span className="text-white font-bold text-xl">M</span>
            </div>
            <span className="font-bold text-2xl tracking-tight text-neutral-900 dark:text-neutral-50">Messego</span>
          </div>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/features" className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Features</Link>
            <Link to="/services" className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Services</Link>
            <Link to="/about" className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">About</Link>
            <Link to="/contact" className="text-sm font-semibold text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Contact</Link>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/auth/login" className="text-sm font-bold text-neutral-900 dark:text-neutral-50 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors">Sign In</Link>
            <Link to="/auth/register" className="px-6 py-2.5 bg-neutral-900 text-white rounded-xl text-sm font-bold hover:bg-neutral-800 transition-all shadow-lg shadow-neutral-900/20">
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-50 dark:bg-neutral-950 border border-neutral-100 dark:border-neutral-800 rounded-full text-xs font-bold text-neutral-600 dark:text-neutral-300 mb-8"
            >
              <Zap className="w-3 h-3 text-amber-500 dark:text-amber-400" />
              The Future of Unified Commerce
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-6xl md:text-7xl font-black tracking-tight text-neutral-900 dark:text-neutral-50 leading-[1.1] mb-8"
            >
              Everything your <span className="text-neutral-400 dark:text-neutral-500">business</span> needs, in one place.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-neutral-500 dark:text-neutral-400 leading-relaxed mb-10 max-w-2xl"
            >
              Messego combines social networking, commerce, career growth, and financial tools into a single, powerful ecosystem. Built for the modern professional.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row items-center gap-4"
            >
              <Link to="/auth/register" className="w-full sm:w-auto px-8 py-4 bg-neutral-900 text-white rounded-2xl font-bold text-lg hover:bg-neutral-800 transition-all shadow-xl shadow-neutral-900/20 flex items-center justify-center gap-2">
                Start for Free
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="#features" className="w-full sm:w-auto px-8 py-4 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 text-neutral-900 dark:text-neutral-50 rounded-2xl font-bold text-lg hover:bg-neutral-50 dark:hover:bg-neutral-950 transition-all text-center">
                Explore Features
              </a>
            </motion.div>
          </div>
        </div>

        {/* Abstract Background Elements */}
        <div className="absolute top-0 right-0 w-1/2 h-full -z-10 opacity-10 pointer-events-none">
          <div className="absolute top-1/4 right-0 w-96 h-96 bg-neutral-900 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-neutral-400 rounded-full blur-[100px]"></div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="py-24 bg-neutral-50 dark:bg-neutral-950">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-20">
            <h2 className="text-4xl font-bold tracking-tight text-neutral-900 dark:text-neutral-50 mb-4">A Unified Ecosystem</h2>
            <p className="text-neutral-500 dark:text-neutral-400">Stop switching between dozens of apps. Messego brings your professional life together.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Social Connect', desc: 'Build your network and share insights with a global community of professionals.', icon: Share2, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/40' },
              { title: 'Smart Commerce', desc: 'A curated marketplace to buy and sell products within the Messego network.', icon: ShoppingBag, color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/40' },
              { title: 'Career Hub', desc: 'Find your next opportunity or hire top talent with our integrated job board.', icon: Briefcase, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/40' },
              { title: 'Business Intelligence', desc: 'Deep analytics and market insights to help your company grow faster.', icon: Building2, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/40' },
              { title: 'Unified Messaging', desc: 'Seamless communication across all modules, from team chats to customer support.', icon: MessageSquare, color: 'text-rose-600 dark:text-rose-400', bg: 'bg-rose-50 dark:bg-rose-950/40' },
              { title: 'Secure Wallet', desc: 'Manage your finances, payments, and assets with enterprise-grade security.', icon: Wallet, color: 'text-neutral-900 dark:text-neutral-50', bg: 'bg-neutral-100 dark:bg-neutral-800' },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white dark:bg-neutral-900 p-8 rounded-[2.5rem] border border-neutral-200 dark:border-neutral-800 shadow-sm dark:shadow-none hover:shadow-xl hover:shadow-neutral-200/50 transition-all group"
              >
                <div className={cn("w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform", feature.bg, feature.color)}>
                  <feature.icon className="w-7 h-7" />
                </div>
                <h3 className="text-xl font-bold text-neutral-900 dark:text-neutral-50 mb-3">{feature.title}</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-square bg-neutral-900 rounded-[3rem] overflow-hidden relative">
                <img
                  src="https://picsum.photos/seed/business/800/800"
                  alt="Business"
                  className="w-full h-full object-cover opacity-60"
                  referrerPolicy="no-referrer"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-neutral-900 to-transparent"></div>
                <div className="absolute bottom-10 left-10 right-10">
                  <div className="bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl">
                    <p className="text-white font-bold text-lg mb-1">Trusted by 10,000+ Companies</p>
                    <p className="text-neutral-300 text-xs">From startups to Fortune 500 enterprises.</p>
                  </div>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-amber-100 rounded-full -z-10 blur-2xl"></div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-blue-100 rounded-full -z-10 blur-2xl"></div>
            </div>

            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-4xl font-bold tracking-tight text-neutral-900 dark:text-neutral-50">Services for Every Stage</h2>
                <p className="text-neutral-500 dark:text-neutral-400 leading-relaxed">Whether you're an individual professional or a growing enterprise, Messego provides the tools you need to succeed in the digital economy.</p>
              </div>

              <div className="space-y-6">
                {[
                  { title: 'Enterprise Solutions', desc: 'Custom workflows, advanced security, and dedicated support for large organizations.' },
                  { title: 'Market Insights', desc: 'Real-time data and AI-driven reports on industry trends and competitor activity.' },
                  { title: 'Financial Infrastructure', desc: 'Global payment processing, automated billing, and secure asset management.' },
                ].map((service) => (
                  <div key={service.title} className="flex gap-4">
                    <div className="mt-1">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-neutral-900 dark:text-neutral-50">{service.title}</h4>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">{service.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4">
                <button className="px-8 py-4 bg-neutral-900 text-white rounded-2xl font-bold hover:bg-neutral-800 transition-all shadow-lg shadow-neutral-900/20">
                  Learn More About Services
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-neutral-900 rounded-[3rem] p-12 md:p-20 text-center relative overflow-hidden">
            <div className="relative z-10 max-w-3xl mx-auto space-y-8">
              <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tight">Ready to transform your professional life?</h2>
              <p className="text-neutral-400 dark:text-neutral-500 text-lg">Join thousands of professionals who are already using Messego to build, connect, and grow.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link to="/auth/register" className="w-full sm:w-auto px-10 py-5 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-50 rounded-2xl font-bold text-xl hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-all">
                  Get Started for Free
                </Link>
                <Link to="/auth/login" className="w-full sm:w-auto px-10 py-5 bg-white/10 text-white border border-white/20 rounded-2xl font-bold text-xl hover:bg-white/20 transition-all backdrop-blur-md">
                  Sign In
                </Link>
              </div>
            </div>

            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full -z-10">
              <div className="absolute top-0 left-1/4 w-64 h-64 bg-blue-500/20 rounded-full blur-[100px]"></div>
              <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-purple-500/20 rounded-full blur-[100px]"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 border-t border-neutral-100 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-6 space-y-12">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-sm">
            <div className="col-span-2 md:col-span-1 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-neutral-900 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">M</span>
                </div>
                <span className="font-bold text-xl tracking-tight text-neutral-900 dark:text-neutral-50">Messego</span>
              </div>
              <p className="text-xs text-neutral-400 dark:text-neutral-500 leading-relaxed">
                Everything your business needs, in one place.
              </p>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-neutral-900 dark:text-neutral-50">Product</h4>
              <ul className="space-y-2 text-neutral-500 dark:text-neutral-400">
                <li><Link to="/services" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Services</Link></li>
                <li><Link to="/features" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Features</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-neutral-900 dark:text-neutral-50">Company</h4>
              <ul className="space-y-2 text-neutral-500 dark:text-neutral-400">
                <li><Link to="/about" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">About Us</Link></li>
                <li><Link to="/careers" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Careers</Link></li>
                <li><Link to="/press" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Press</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-neutral-900 dark:text-neutral-50">Support</h4>
              <ul className="space-y-2 text-neutral-500 dark:text-neutral-400">
                <li><Link to="/faq" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">FAQ</Link></li>
                <li><Link to="/contact" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Contact</Link></li>
              </ul>
            </div>

            <div className="space-y-3">
              <h4 className="font-bold text-xs uppercase tracking-wider text-neutral-900 dark:text-neutral-50">Legal</h4>
              <ul className="space-y-2 text-neutral-500 dark:text-neutral-400">
                <li><Link to="/privacy" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Terms of Service</Link></li>
                <li><Link to="/cookies" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-neutral-100 dark:border-neutral-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-neutral-400 dark:text-neutral-500">© 2026 Messego Inc. All rights reserved.</p>
            <div className="flex gap-6 text-xs text-neutral-400 dark:text-neutral-500">
              <Link to="/privacy" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Terms</Link>
              <Link to="/cookies" className="hover:text-neutral-900 dark:hover:text-neutral-50 transition-colors">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
